import { useEffect, useMemo, useState } from 'react';
import APIList from '../http/ApiList';
import { Button, Form, Input, message, Select, Table } from 'antd';

const useGanttFilter = (ganttRef: any, optimizationId: string) => {
  const loadFilterConfig = (optimizationId: string) => {
    if (!optimizationId) return;
    APIList.getGanttConfig()
      .post({
        optimizationId,
      })
      .then((result) => {
        setFilterConfig(result.ganttConfig);
        const ganttConfig = JSON.parse(localStorage.getItem('ganttConfig'));
        if (!ganttConfig) return;
        const config = result.ganttConfig.find((gc) => gc.ganttConfigName === ganttConfig.ganttConfigName);
        if (!config) return;
        applyFilterConfig(config.columnConfig, null);
      });
  };

  const [loadModalVisible, setLoadModalVisible] = useState(false);
  const [saveModalVisible, setSaveModalVisible] = useState(false);
  const [filterConfig, setFilterConfig] = useState([]);
  // 初始化所有过滤状态
  const [filterStatus, setFilterStatus] = useState({
    check: [],
    discipline: [],
    area: [],
    package: [],
  });
  const isFilterUsed = useMemo(() => Object.values(filterStatus).some((v) => !!v.length), [filterStatus]);
  const [form] = Form.useForm();
  useEffect(() => {
    setFilterStatus({
      check: [],
      discipline: [],
      area: [],
      package: [],
    });
    loadFilterConfig(optimizationId);
  }, [optimizationId]);

  const openLoadFilterModal = () => {
    setLoadModalVisible(true);
  };
  const openSaveFilterModal = () => {
    form.resetFields(); // 重置表单
    setSaveModalVisible(true);
  };

  // 保存过滤器配置
  const handleSaveFilterConfig = async () => {
    try {
      // 验证表单
      await form.validateFields();
      const { configName, useType } = form.getFieldsValue();

      // 准备要保存的配置数据
      const configData = {
        columnConfig: JSON.stringify(filterStatus),
        ganttConfigName: configName,
        useType,
      };
      APIList.setGanttConfig()
        .post({
          optimizationId,
          isDelete: false,
          ...configData,
        })
        .then((result) => {
          message.success('Update Gantt Configuration Success.');
          loadFilterConfig(optimizationId);
          localStorage.setItem('ganttConfig', JSON.stringify(configData));
          setSaveModalVisible(false);
        });
    } catch (error) {
      console.error('save failed:', error);
    }
  };
  // Gantt Filter Logic
  const applyFilterConfig = (columnConfigString: string, configObj: any) => {
    try {
      const config = JSON.parse(columnConfigString);

      // 确保所有字段都有正确的格式
      const newFilterStatus = {
        check: config.check !== undefined ? config.check : [],
        discipline: Array.isArray(config.discipline) ? config.discipline : [],
        area: Array.isArray(config.area) ? config.area : [],
        package: Array.isArray(config.package) ? config.package : [],
      };

      setFilterStatus(newFilterStatus);
      setLoadModalVisible(false);
      if (configObj) {
        localStorage.setItem('ganttConfig', JSON.stringify(configObj));
      }
      message.success('Apply Gantt Configuration Success.');
    } catch (error) {
      console.error('Failed to parse filter config:', error);
      message.error('Apply Gantt Configuration Failed.');
    }
  };

  const handleRemoveFilter = () => {
    setFilterStatus({
      check: [],
      discipline: [],
      area: [],
      package: [],
    });
    localStorage.removeItem('ganttConfig');
    setLoadModalVisible(false);
  };

  const filterConfigColumns = [
    {
      title: 'Configuration Name',
      dataIndex: 'ganttConfigName',
      key: 'ganttConfigName',
      width: 200,
    },
    {
      title: 'Use Type',
      dataIndex: 'useType',
      key: 'useType',
      width: 100,
      render: (useType) => <span>{useType === '2' ? 'Public' : 'Private'}</span>,
    },
    {
      title: 'User Name',
      dataIndex: 'userName',
      key: 'userName',
      width: 120,
    },
    {
      title: '',
      key: '',
      width: 100,
      render: (_, record) => (
        <span>
          <Button
            type="link"
            size="small"
            onClick={() => {
              applyFilterConfig(record.columnConfig, record);
            }}
          >
            Apply
          </Button>
          {record.isOwner && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                APIList.setGanttConfig()
                  .post({
                    columnConfig: record.columnConfig,
                    useType: record.useType,
                    ganttConfigName: record.ganttConfigName,
                    isDelete: true,
                    optimizationId: optimizationId,
                  })
                  .then((result) => {
                    loadFilterConfig(optimizationId);
                    handleRemoveFilter();
                    message.success('Save Configuration Success.');
                  });
              }}
            >
              Delete
            </Button>
          )}
        </span>
      ),
    },
  ];
  // 应用过滤器函数
  const applyFilter = () => {
    const gantt = ganttRef.current.ganttInstance();
    const taskStore = gantt.project?.taskStore;
    if (!gantt && !taskStore) return;

    const { check, discipline, area, package: pkg } = filterStatus;

    const filters: any[] = [];
    if (Array.isArray(check) && check.length > 0) {
      filters.push({
        id: 'check',
        property: 'check',
        operator: 'isIncludedIn',
        value: check,
      });
    }

    // discipline（多选）
    if (Array.isArray(discipline) && discipline.length > 0) {
      filters.push({
        id: 'discipline',
        property: 'discipline',
        operator: 'isIncludedIn',
        value: discipline,
      });
    }

    // area（多选）
    if (Array.isArray(area) && area.length > 0) {
      filters.push({
        id: 'area',
        property: 'area',
        operator: 'isIncludedIn',
        value: area,
      });
    }

    // package（多选）
    if (Array.isArray(pkg) && pkg.length > 0) {
      filters.push({
        id: 'package',
        property: 'package',
        operator: 'isIncludedIn',
        value: pkg,
      });
    }

    // 应用 / 清空
    if (filters.length === 0) {
      taskStore.clearFilters();
    } else {
      taskStore.filter({
        filters,
        replace: true,
      });
    }
  };

  // 修改 updateFilter 函数正确处理 Bryntum 返回的数据
  // 修改 updateFilter 函数，添加值比较逻辑
  const updateFilter = (field: string, value: any) => {
    setFilterStatus(
      (prev) => {
        let newArray = [];

        // 处理 Bryntum 返回的各种数据格式
        if (value === null || value === undefined || value === '') {
          // 空值，清空数组
          newArray = [];
        } else if (Array.isArray(value)) {
          // 如果已经是数组，直接使用
          newArray = value;
        } else if (value && typeof value === 'object') {
          // Bryntum 可能返回对象，需要提取值
          // 如果是 {value: [...]} 格式
          if (value.value && Array.isArray(value.value)) {
            newArray = value.value;
          }
          // 如果是 {selected: [...]} 格式
          else if (value.selected && Array.isArray(value.selected)) {
            newArray = value.selected;
          }
          // 如果是普通对象，尝试提取所有值
          else {
            newArray = Object.values(value).filter((v) => v !== null && v !== undefined);
          }
        } else if (typeof value === 'string' || typeof value === 'number') {
          // 单个值，转为数组
          newArray = [value];
        } else if (typeof value === 'boolean') {
          // 对于 check 字段，布尔值直接返回
          newArray = [value];
        }

        // 检查数组是否真的改变了（深度比较）
        const prevArray = prev[field];
        if (Array.isArray(prevArray) && Array.isArray(newArray)) {
          // 检查长度是否相同
          if (prevArray.length === newArray.length) {
            // 检查每个元素是否相同（浅比较）
            const allSame = newArray.every((item, index) => item === prevArray[index]);
            if (allSame) {
              return prev; // 值没有变化，返回原状态
            }
          }
        }

        return {
          ...prev,
          [field]: newArray,
        };
      },
      // }
    );
  };

  // 切换 tab 的时候，需要同步多个 gantt 的滤器值
  useEffect(() => {
    if (!ganttRef.current) return;
    applyFilter();
  }, [ganttRef.current, filterStatus]);

  // 更新滤器，需要同步到 State 中
  useEffect(() => {
    if (!ganttRef.current) return;
    const taskStore = ganttRef.current.ganttInstance()?.project?.taskStore;
    if (!taskStore) return;
    const filterCallback = () => {
      // 获取所有过滤器的值
      const checkFilter = taskStore.filters.get('check') as any;
      const disciplineFilter = taskStore.filters.get('discipline') as any;
      const areaFilter = taskStore.filters.get('area') as any;
      const packageFilter = taskStore.filters.get('package') as any;
      // 同步 check 过滤器
      if (checkFilter) {
        updateFilter('check', checkFilter.value);
      } else {
        // 如果没有 check 过滤器，设置为 null
        updateFilter('check', []);
      }

      // 同步 discipline 过滤器
      if (disciplineFilter) {
        updateFilter('discipline', disciplineFilter.value);
      } else {
        // 如果没有 discipline 过滤器，设置为空数组
        updateFilter('discipline', []);
      }

      // 同步 area 过滤器
      if (areaFilter) {
        updateFilter('area', areaFilter.value);
      } else {
        updateFilter('area', []);
      }

      // 同步 package 过滤器
      if (packageFilter) {
        updateFilter('package', packageFilter.value);
      } else {
        updateFilter('package', []);
      }
    };
    taskStore.on('filter', filterCallback);
    return () => {
      taskStore.un('filter', filterCallback);
    };
  }, [ganttRef.current]);

  const SaveFilterModalContent = () => (
    <Form form={form} layout="vertical">
      <Form.Item
        rules={[
          { required: true, message: 'Name Required' },
          {
            validator: (_, value) => {
              if (!value) return Promise.resolve();
              // 检查配置名是否已存在
              const existingConfig = filterConfig.find((config) => config.ganttConfigName === value);
              if (existingConfig && !existingConfig.isOwner) {
                // 如果配置已存在且不是当前用户的，提示错误
                return Promise.reject(new Error('The name already exists. Please use a different name.'));
              }
              return Promise.resolve();
            },
          },
        ]}
        label="Configuration Name"
        name="configName"
      >
        <Input
          onChange={(e) => {
            const configName = e.target.value;
            // 检查配置名是否已存在
            const existingConfig = filterConfig.find((config) => config.ganttConfigName === configName);
            // 在 filterConfig 中查找匹配的配置
            if (existingConfig) {
              // 如果找到匹配的配置，设置 useType 为配置中的值
              form.setFieldValue('useType', existingConfig.useType || '1');
            } else {
              // 如果没有找到匹配的配置，清空 useType
              form.setFieldValue('useType', undefined);
            }
            // // 如果配置已存在且不是当前用户的，则设置 disabled
            // console.log('exist', existingConfig);
            // console.log('isOwner', existingConfig?.isOwner);
            // form.setFieldValue('canNotSave', !!existingConfig);
          }}
        />
      </Form.Item>
      <Form.Item label="Use Type" name="useType" rules={[{ required: true, message: 'Use Type Required.' }]}>
        <Select
          options={[
            {
              value: '1',
              label: 'Private',
            },
            {
              value: '2',
              label: 'Public',
            },
          ]}
        />
      </Form.Item>
      {/* <Form.Item hidden name="canNotSave">
        <Input type="hidden" />
      </Form.Item> */}
    </Form>
  );

  const LoadFilterModalContent = () => (
    <div style={{ position: 'relative' }}>
      <Table
        columns={filterConfigColumns}
        dataSource={filterConfig}
        rowKey={(record, index) => index.toString()}
        pagination={{ pageSize: 10 }}
        size="small"
      />
      {
        // 如果已经设置了滤器，则显示按钮，否则按钮不显示
        isFilterUsed && (
          <Button type="primary" style={{ position: 'absolute', bottom: 10 }} onClick={handleRemoveFilter}>
            Clear Applying Filter
          </Button>
        )
      }
    </div>
  );

  return {
    filterConfig,
    setFilterConfig,
    loadFilterConfig,
    SaveFilterModalContent,
    LoadFilterModalContent,
    openLoadFilterModal,
    openSaveFilterModal,
    saveModalVisible,
    setSaveModalVisible,
    loadModalVisible,
    setLoadModalVisible,
    handleSaveFilterConfig,
  };
};

export default useGanttFilter;
